/* LIMITY ÚVĚRU NA VLASTNÍ BYDLENÍ PODLE ZEMĚ — LTV a věková výjimka.
 * ============================================================================
 *
 * Majitel 30. 9. 2026: „když je tam hypotéka, klientovi je 38 let, tak třeba
 * už nejde 10 %". Kolik vlastních zdrojů klient potřebuje, je první věta,
 * kterou poradce u hypotéky řekne — a ta věta musí sedět na zákon, ne na odhad.
 *
 * ČESKO — ZÁVAZNÉ (ČNB)
 * ---------------------
 *   LTV 80 %, 90 % pro žadatele MLADŠÍ 36 LET, kteří si pořizují vlastní bydlení.
 *   Opatření obecné povahy ČNB č. I/2021 (stanovení horní hranice úvěrových
 *   ukazatelů), ve znění účinném od 29. 11. 2023. Ověřeno 30. 9. 2026:
 *   https://www.cnb.cz/cs/financni-stabilita/makroobezretnostni-politika/stanoveni-horni-hranice-uverovych-ukazatelu/ltv/index.html
 *   (znění: „80 % (90 % pro žadatele mladší 36 let pořizující si vlastní bydlení)“)
 *
 *   ⚠️ MLADŠÍ 36 LET = dovršený věk nejvýš 35. Do 30. 9. 2026 stálo v liště
 *   `vek <= 36`, takže 36letý klient dostal 90 % — o rok víc, než dovoluje ČNB.
 *   Kalkulačka mu tak slíbila hypotéku s 10 % vlastních zdrojů, kterou banka
 *   dát nesmí.
 *
 *   ⚠️ U SPOLEČNÉ ŽÁDOSTI stránka ČNB neříká, jestli stačí jeden mladý
 *   žadatel. Banky v praxi berou „alespoň jeden“ — tady se ale počítá jen
 *   s věkem prvního klienta analýzy (přísnější čtení). Rozhodne majitel.
 *
 *   Limity DSTI / DTI jsou u vlastního bydlení deaktivované (jen doporučení),
 *   viz `data/config2026.js` → HYPO. Tady se jimi nic nezakazuje.
 *
 * SLOVENSKO — NBS (ověřeno 4. 10. 2026, podklad beplan-pracovni/2026-10-04-priprava/nbs.md)
 * ---------------
 *   DO 31. 12. 2026 (opatrenie NBS č. 10/2016 § 7 v znení 7/2018): LTV nejvýš
 *   90 %, úvěry nad 80 % smějí tvořit jen omezený podíl nových úvěrů banky
 *   (kvóta). Pro jednotlivého klienta je JISTÝCH 80 %; 90 % záleží na kvótě
 *   banky, a proto ho appka NESLIBUJE. Věk nerozhoduje.
 *
 *   OD 1. 1. 2027 (novela schválená Bankovou radou NBS 28. 9. 2026, § 7 a § 9d
 *   pracovního znění; NBS, otázky k LTV, aktualizace 2. 10. 2026): 90 % jen pro
 *   PRVNÍ BYDLENÍ — žadatel má k datu žádosti nejvýš 35 let, NEVLASTNÍ
 *   rezidenční nemovitost a úvěr je na koupi / výstavbu; u společné žádosti
 *   musí podmínky splnit KAŽDÝ. Ostatní 80 %. (Od 1. 7. 2027 70 % na třetí
 *   a další nemovitost — appka nemovitosti klienta v SR nepočítá, proto na
 *   sedmdesátku sama nesahá.)
 *   Rozhodnutí majitele 4. 10. 2026 („ok vse dle doporučení“, bod 8).
 *
 * NEZNÁMÉ ≠ NÁROK: neznámý věk, neznámé „první bydlení“ i neznámý věk dalšího
 * žadatele = přísnější limit.
 *
 * Čistá funkce bez Reactu.
 */
import { CONFIG_2026 } from "@/data/config2026";
import { normZeme } from "@/lib/tenantMena";

/** Věková hranice ČNB: 90 % mají žadatelé MLADŠÍ než tento věk. */
export const VEK_MLADSI_NEZ = 36;

export const LTV_PRAVIDLA = Object.freeze({
  CZ: Object.freeze({
    zakladniPct: CONFIG_2026.HYPO.LTV_NAD_36_VLASTNI,
    mladiPct: CONFIG_2026.HYPO.LTV_DO_36_VLASTNI,
    mladsiNez: VEK_MLADSI_NEZ,
    zavazne: true,
    zdroj: "ČNB, opatření obecné povahy č. I/2021 ve znění od 29. 11. 2023",
    overeno: "2026-09-30",
  }),
  SK: Object.freeze({
    zakladniPct: 80,
    mladiPct: null,
    mladsiNez: null,
    /* Strop, kam se dá dojít jen v kvótě banky. Pro obrazovku ne jako limit. */
    kvotaPct: 90,
    /* PRVNÍ BYDLENÍ od 1. 1. 2027: 90 % pro žadatele nejvýš 35leté (dovršený
     * věk k datu žádosti), bez rezidenční nemovitosti. */
    prvniBydleni: Object.freeze({ odRoku: 2027, pct: 90, vekNejvys: 35 }),
    zavazne: true,
    zdroj: "NBS, opatrenie č. 10/2016 § 7 (do 31. 12. 2026: 80 %, 90 % len v kvóte banky) a § 9d (od 1. 1. 2027: 90 % prvé bývanie do 35 rokov)",
    overeno: "2026-10-04",
  }),
});

/** Rok, ve kterém klient o úvěr žádá (koupě), když ho volající nezná: letos. */
const rokLetos = () => new Date().getFullYear();

/* KDO STANOVUJE PRAVIDLA A JAK O NICH SMÍME MLUVIT (strážce grafů 4. 10. 2026).
 *   CZ  LTV je závazný limit ČNB; DSTI a DTI jsou jen DOPORUČENÍ ČNB
 *       (config2026 → HYPO: horní hranice deaktivované). Slovo „limit“
 *       patří jen k LTV — u DSTI/DTI by deaktivované doporučení vydávalo
 *       za zákaz.
 *   SK  regulátor je NBS. Limity DSTI/DTI NBS jsou podle všeho ZÁVAZNÉ
 *       a DSTI se počítá jinak (z příjmu po odečtení životního minima) —
 *       NEOVĚŘENO proti aktuálnímu znění, rozhodne majitel / SK partner.
 *       Do té doby slovenský tenant vidí u DSTI/DTI jen hodnotu, žádný
 *       verdikt a nikdy nálepku „ČNB“. */
export const REGULATOR_UVERU = Object.freeze({
  CZ: Object.freeze({ nazev: "ČNB", verdiktDstiDti: true }),
  SK: Object.freeze({ nazev: "NBS", verdiktDstiDti: false }),
});

/** Regulátor úvěrů podle země tenanta; neznámá země = CZ. */
export function regulatorUveru(zeme) {
  return REGULATOR_UVERU[normZeme(zeme)] || REGULATOR_UVERU.CZ;
}

const kladneCislo = (x) => {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Limit LTV pro klienta.
 *
 * @param {object} p
 * @param {number|null} p.vek   dovršený věk K DATU ŽÁDOSTI (u cíle v budoucnu
 *                              věk v roce koupě); neznámý = null
 * @param {string}      p.zeme  země tenanta (CZ / SK); neznámá = CZ
 * @param {number}      [p.rokKoupe] rok žádosti o úvěr; bez něj letos (SK:
 *                              rozhoduje, jestli platí režim od 1. 1. 2027)
 * @param {boolean|null} [p.prvniBydleni] SK od 2027: klient NEVLASTNÍ
 *                              rezidenční nemovitost (true). Neznámé = ne.
 * @param {Array<number|null>} [p.vekyDalsich] SK od 2027: věk dalších žadatelů
 *                              k datu žádosti (podmínku musí splnit každý);
 *                              neznámý věk = podmínka nesplněna
 * @returns {{ limitPct: number, vekZnamy: boolean, vekRozhoduje: boolean, mladi: boolean, zeme: string }}
 *   SK navíc `rezim` („do2026“ | „od2027“).
 *
 * ⚠️ BEZ VĚKU se bere PŘÍSNĚJŠÍ hranice. Slíbit 90 % klientovi, jehož věk
 * neznáme, znamená slíbit hypotéku, kterou banka dát nemusí. `vekZnamy:false`
 * je pro obrazovku, aby to řekla nahlas.
 */
export function ltvLimit({ vek, zeme, rokKoupe, prvniBydleni = null, vekyDalsich = [] } = {}) {
  const kod = normZeme(zeme);
  const pravidlo = LTV_PRAVIDLA[kod] || LTV_PRAVIDLA.CZ;
  const v = kladneCislo(vek);
  /* SLOVENSKO OD 1. 1. 2027 — 90 % jen první bydlení do 35 let (každý žadatel). */
  if (pravidlo.prvniBydleni) {
    const rok = Number.isFinite(Number(rokKoupe)) && Number(rokKoupe) > 0 ? Math.floor(Number(rokKoupe)) : rokLetos();
    const P = pravidlo.prvniBydleni;
    if (rok < P.odRoku) {
      return { limitPct: pravidlo.zakladniPct, vekZnamy: v !== null, vekRozhoduje: false, mladi: false, zeme: kod, rezim: "do2026" };
    }
    const dalsi = Array.isArray(vekyDalsich) ? vekyDalsich : [];
    const vekOk = (x) => kladneCislo(x) !== null && Math.floor(Number(x)) <= P.vekNejvys;
    const mladi = prvniBydleni === true && vekOk(v) && dalsi.every(vekOk);
    return { limitPct: mladi ? P.pct : pravidlo.zakladniPct, vekZnamy: v !== null, vekRozhoduje: true, mladi, zeme: kod, rezim: "od2027" };
  }
  const vekRozhoduje = pravidlo.mladiPct !== null && pravidlo.mladsiNez !== null;
  if (!vekRozhoduje) {
    return { limitPct: pravidlo.zakladniPct, vekZnamy: v !== null, vekRozhoduje: false, mladi: false, zeme: kod };
  }
  if (v === null) {
    return { limitPct: pravidlo.zakladniPct, vekZnamy: false, vekRozhoduje: true, mladi: false, zeme: kod };
  }
  const mladi = v < pravidlo.mladsiNez;
  return {
    limitPct: mladi ? pravidlo.mladiPct : pravidlo.zakladniPct,
    vekZnamy: true,
    vekRozhoduje: true,
    mladi,
    zeme: kod,
  };
}

/* ── SLOVENSKO — DTI (strop VŠECH dluhů k ročnímu příjmu) ─────────────────────
 * Rozhodnutí majitele 4. 10. 2026 („ok necham tvoje doporučeni“, 🔴 3): u SK
 * tenanta se bydlení neměří českým násobkem ceny (ČNB 9×), ale závazným
 * limitem NBS na DLUH.
 *
 * ZDROJ (ověřeno 4. 10. 2026, podklad beplan-pracovni/2026-10-04-priprava/nbs.md):
 *   opatrenie NBS č. 10/2016 § 8a ods. 1–2 a 7–8 (v znení 5/2022, účinné od
 *   1. 1. 2023); zákon č. 90/2016 Z. z. § 8 ods. 3 a 7; NBS, stránka DTI
 *   (https://nbs.sk/en/financial-stability/fs-instruments/dti/) — tabulka věku
 *   a vzorové příklady (134 400 €, 113 400 €, 121 800 €; 81 000 € z otázek NBS
 *   k úvěrům zasahujícím do důchodu). Všechny čtyři sedí na euro (test).
 *
 *   · všechny úvěry žadatele dohromady (nový + zůstatky ostatních) ≤
 *     limit × 12 × čistý měsíční příjem,
 *   · limit 8; od dovršených 41 let −0,25 za každý rok nad 40, ale JEN když
 *     úvěr skončí po dovršení 65 let (floor(věk) + splatnost ≥ 65), nejméně 3,
 *   · dva žadatelé: limit každému zvlášť a součet (= vážený průměr NBS).
 *
 * ⚠️ NEZNÁMÉ ≠ NULA. Neznámý věk se nesmí stát věkem 0 (`Number(null) === 0`)
 * — počítá se s 8 a jde ven `vekChybi`. Neznámé zůstatky jiných úvěrů nejsou
 * nula — `stav: "dluhyChybi"` a strop se netvrdí.
 * Nevyčerpané limity kreditek a kontokorentů (20 % podle § 8a ods. 8) appka
 * neeviduje, výpočet je o ně mírnější než banka. Limity jsou MINIMUM, banka
 * smí být přísnější. */
export const DTI_PRAVIDLA = Object.freeze({
  SK: Object.freeze({
    zakladni: 8,             // × roční čistý příjem, VŠECHNY úvěry dohromady
    odVeku: 41,              // od dovršených 41 let …
    vekKonceSplatnosti: 65,  // … pokud floor(věk) + splatnost ≥ 65 (úvěr končí po dovŕšení 65)
    krokZaRok: 0.25,
    minimum: 3,
    podilNevycerpanychLimitu: 0.2, // appka limity karet neeviduje — jen pro úplnost
    zavazne: true,
    zdroj: "NBS, opatrenie č. 10/2016 § 8a (v znení 5/2022, účinné od 1. 1. 2023)",
    overeno: "2026-10-04",
  }),
});

const neznamaHodnota = (x) => x === null || x === undefined || x === "" || typeof x === "boolean" || !Number.isFinite(Number(x));

/**
 * Limit DTI jedné osoby (násobek ročního čistého příjmu).
 * @param {{ vekPriZadosti?: number|null, splatnostLet?: number|null }} p
 * @returns {{ limit: number, vekZnamy: boolean, snizeno: boolean }}
 */
export function dtiLimitOsoby({ vekPriZadosti, splatnostLet } = {}) {
  const P = DTI_PRAVIDLA.SK;
  if (neznamaHodnota(vekPriZadosti) || Number(vekPriZadosti) < 0) return { limit: P.zakladni, vekZnamy: false, snizeno: false };
  const vek = Math.floor(Number(vekPriZadosti));
  /* Neznámá splatnost = úvěr MŮŽE skončit po 65 (přísnější čtení). */
  const doDuchodu = neznamaHodnota(splatnostLet) ? true : vek + Math.round(Number(splatnostLet)) >= P.vekKonceSplatnosti;
  if (vek < P.odVeku || !doDuchodu) return { limit: P.zakladni, vekZnamy: true, snizeno: false };
  return { limit: Math.max(P.minimum, P.zakladni - P.krokZaRok * (vek - 40)), vekZnamy: true, snizeno: true };
}

/**
 * Nejvyšší nový úvěr podle limitu DTI NBS.
 *
 * @param {object} p
 * @param {Array<{prijemMesicne:number, vekDnes?:number|null}>} p.osoby žadatelé
 *   (osoba bez příjmu se nepočítá)
 * @param {number} [p.rokyDoCile]   za kolik let klient o úvěr požádá (věk při žádosti = dnes + roky)
 * @param {number} [p.splatnostLet] splatnost nového úvěru (výchozí 30)
 * @param {number|null} [p.ostatniDluhy] součet zůstatků ostatních úvěrů;
 *   vynechané = 0 (žádné), `null` / NaN = NEZNÁMÉ → `stav: "dluhyChybi"`
 * @returns {{ stav: "ok"|"chybiPrijem"|"dluhyChybi", maxDluh?: number,
 *   maxUver?: number|null, limitVazeny?: number, vekChybi?: boolean,
 *   vycerpano?: boolean, dluhy?: number|null }}
 *   maxDluh  = limit pro VŠECHNY úvěry dohromady (věta „všetky úvery … = X“),
 *   maxUver  = co z něj zbývá na nový úvěr po ostatních dluzích.
 */
export function maxUverDti({ osoby = [], rokyDoCile = 0, splatnostLet = 30, ostatniDluhy = 0 } = {}) {
  let maxDluh = 0;
  let prijemRok = 0;
  let vekChybi = false;
  const roky = Number.isFinite(Number(rokyDoCile)) && Number(rokyDoCile) > 0 ? Number(rokyDoCile) : 0;
  for (const o of Array.isArray(osoby) ? osoby : []) {
    const p = kladneCislo(o && o.prijemMesicne);
    if (p === null) continue;
    const vekDnes = o ? o.vekDnes : null;
    const vekZadost = neznamaHodnota(vekDnes) ? null : Number(vekDnes) + roky;
    const l = dtiLimitOsoby({ vekPriZadosti: vekZadost, splatnostLet });
    if (!l.vekZnamy) vekChybi = true;
    maxDluh += l.limit * 12 * p;
    prijemRok += 12 * p;
  }
  if (!(prijemRok > 0)) return { stav: "chybiPrijem" };
  const limitVazeny = maxDluh / prijemRok;
  if (neznamaHodnota(ostatniDluhy)) {
    return { stav: "dluhyChybi", maxDluh, maxUver: null, limitVazeny, vekChybi, vycerpano: false, dluhy: null };
  }
  const dluhy = Math.max(0, Number(ostatniDluhy));
  return { stav: "ok", maxDluh, maxUver: Math.max(0, maxDluh - dluhy), limitVazeny, vekChybi, vycerpano: dluhy > 0 && dluhy >= maxDluh, dluhy };
}

/**
 * Nejmenší vlastní zdroje, aby LTV sedělo do limitu. Neznámá cena = null.
 * Zaokrouhluje NAHORU — o korunu méně už by limit přetekl.
 */
export function minimalniVlastniZdroje(cena, limitPct) {
  const c = kladneCislo(cena);
  const l = Number(limitPct);
  if (c === null || !Number.isFinite(l) || l <= 0 || l > 100) return null;
  /* `(100 − l) / 100`, ne `1 − l / 100`: 5 200 000 × (1 − 0,8) vyjde v plovoucí
     čárce 1 040 000,0000000002 a zaokrouhlení nahoru z toho udělá o korunu víc. */
  return Math.ceil((c * (100 - l)) / 100 - 1e-9);
}
